import * as React from 'react';

import {
  Notice,
} from '@affiliates/AspireUI';

import {
  DashboardQueriesStatus,
  useDashboardQueries,
} from '@affiliates/hooks';
import { IEnabledOfferSources } from '@affiliates/types';
import { useState } from 'react';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { CircleDollarIcon, UserGroupIcon, WalletIcon } from '@revfluence/fresh-icons/solid/esm';
import { MainMetric, WidgetContainer } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { IDateRangeSettings } from '@frontend/app/components/DateFilter';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import { useHistory } from 'react-router-dom';
import { GET_ALL_PROGRAMS } from '@frontend/app/queries';
import { logger } from '@common';
import { some } from 'lodash';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2';
import Tag from '@frontend/app/refresh-components/Tag';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipArrow,
} from '@frontend/applications/Social/components/tooltip';
import { DateRangeOptions } from '@frontend/app/components/DateFilter';
import { useApolloClientForAspirex } from '../../hooks/useApolloClientForAspirex';
import { RefreshOfferTable } from '../../components/OfferTable/RefreshOfferTable';
import { OFFER_SOURCE, OFFER_STATUS_TYPE, STATS_OFFER_TYPE } from '../../types/globalTypes';
import { IDashboardOffer, ShopifyScopesMissing } from '../../components';

interface IProps {
  baseUri: string;
  clientName: string;
  dateRangeSettings: IDateRangeSettings;
  shopifyAppId: string;
  sources: IEnabledOfferSources;
  selectedFilters: OFFER_STATUS_TYPE[];
  setSelectedFilters: (filters: OFFER_STATUS_TYPE[]) => void;
  selectedOfferType: STATS_OFFER_TYPE;
  setSelectedOfferType: (type: STATS_OFFER_TYPE) => void;
  selectedPrograms: number[];
  setSelectedPrograms: (programs: number[]) => void;
  setIsDrawerOpen: (open: boolean) => void;
  offerNumericFilters: Record<string, string[]>;
  setOfferNumericFilters: (filters: Record<string, string[]>) => void;
  offerSelectedStatusFilters: string[];
  setOfferSelectedStatusFilters: (filters: string[]) => void;
}

interface StatCardProps {
  title: string;
  bgColor: string;
  widgetIcon: React.ReactNode;
  value: number;
  metricType: 'number' | 'currency' | 'count';
  trendIncrease?: number;
  subHeading?: string;
  primaryPopoverText?: string;
  secondaryPopoverText?: string;
  isLoading?: boolean;
  tooltip?: string;
}
export const dummyOfferTableColumns = [
  { id: 'offerName', header: 'Name', accessorKey: 'offerName' },
  { id: 'programId', header: 'Connected Project', accessorKey: 'programId' },
  { id: 'url', header: 'URL', accessorKey: 'url' },
  { id: 'status', header: 'Status', accessorKey: 'status' },
  { id: 'members', header: 'Members', accessorKey: 'members' },
  { id: 'clicks', header: 'Clicks', accessorKey: 'clicks' },
  { id: 'conversions', header: 'Conversions', accessorKey: 'conversions' },
  { id: 'salesAmount', header: 'Sales Amt', accessorKey: 'salesAmount' },
  { id: 'payoutEarned', header: 'Commissions Earned', accessorKey: 'payoutEarned' },
  { id: 'payoutMade', header: 'Payout', accessorKey: 'payoutMade' },
  { id: 'avgSale', header: 'Avg. Sale', accessorKey: 'avgSale' },
  { id: 'createdDate', header: 'Created On', accessorKey: 'createdDate' },
];
function StatCard({
  title,
  bgColor,
  widgetIcon,
  value,
  metricType,
  trendIncrease,
  subHeading,
  primaryPopoverText,
  secondaryPopoverText,
  isLoading,
  tooltip,
}: StatCardProps) {
  const loadingContent = (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
    </div>
  );
  const mainContent = (
    <MainMetric
      value={value}
      size="small"
      icon={null}
      metricType={metricType}
      trend={trendIncrease}
      subHeading={subHeading}
      className="p-2 pt-0 -mt-2"
      popoverTextPrimary={primaryPopoverText}
      popoverTextSecondary={secondaryPopoverText}
    />
  );
  return (
    <WidgetContainer widgetTitle={title} bgColor={bgColor} widgetIcon={widgetIcon} tooltip={tooltip}>
      {isLoading ? loadingContent : mainContent}
    </WidgetContainer>
  );
}

const OFFER_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
  PAUSED: 'Paused',
  EXPIRED: 'Expired',
};

const OFFER_TYPE_LABELS: Record<string, string> = {
  LINK_OFFER: 'Link Offer',
  PROMO_OFFER: 'Promo Offer',
  ALL_OFFERS: 'All Offers',
};

function getProgramName(programs: GetAllPrograms_programs[] | undefined, id: number): string {
  const program = programs?.find((p) => p.id === id);
  return program ? `Project: ${program.title}` : `Project: ${id}`;
}

export const RefreshDashboardPage: React.FC<Readonly<IProps>> = (props) => {
  const {
    baseUri,
    clientName,
    dateRangeSettings,
    shopifyAppId,
    sources,
    selectedFilters,
    setSelectedFilters,
    selectedOfferType,
    setSelectedOfferType,
    selectedPrograms,
    setSelectedPrograms,
    setIsDrawerOpen,
    offerNumericFilters,
    setOfferNumericFilters,
    offerSelectedStatusFilters,
    setOfferSelectedStatusFilters,
  } = props;

  const { archiveOffer } = useClientFeatures();
  const history = useHistory();
  const aspirexApolloClient = useApolloClientForAspirex();
  const [programs, setPrograms] = useState<GetAllPrograms_programs[]>();
  const queries = useDashboardQueries(
    baseUri,
    clientName,
    dateRangeSettings,
    sources,
    selectedOfferType,
    selectedFilters,
    archiveOffer,
  );
  React.useEffect(() => {
    if (queries.status === DashboardQueriesStatus.Ready) {
      dateRangeSettings.setEarliestDate(queries.advertiser.createdDate);
    }
    aspirexApolloClient.query({
      query: GET_ALL_PROGRAMS,
    }).then((data) => {
      setPrograms(data.data.programs);
    }).catch((error) => {
      logger.error(error);
    });
  }, [queries, dateRangeSettings, aspirexApolloClient]);

  const getDashboardPageContents = () => {
    switch (queries.status) {
      case DashboardQueriesStatus.Loading:
        return (
          <>
            <div className="grid grid-cols-1 gap-4 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 p-4">
              <StatCard
                title="Members"
                bgColor="bg-purple-5"
                widgetIcon={<UserGroupIcon className="text-grey-0" fontSize={20} />}
                value={0}
                metricType="count"
                subHeading="893 with Conversions"
                tooltip="The number of members added across all offers. The metric below shows how many of them have driven at least one conversion."
                isLoading
              />
              <StatCard
                title="Conversions"
                bgColor="bg-blue-5"
                widgetIcon={<FontAwesomeIcon icon={faGlobe} className="text-white" fontSize={20} />}
                value={0}
                metricType="number"
                tooltip="Shows the number of conversions driven by members across all offers. The metric below shows the number of clicks.
"
                isLoading
              />
              <StatCard
                title="Total Sales"
                bgColor="bg-teal-5"
                widgetIcon={<CircleDollarIcon className="text-white" fontSize={20} />}
                value={0}
                metricType="currency"
                tooltip="Shows the total sales driven by members across all offers. The metric below shows the average sales per member."
                isLoading
              />
              <StatCard
                title="Commissions Earned"
                bgColor="bg-lime-5"
                widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                value={0}
                metricType="currency"
                tooltip="Earned: Shows the total commissions earned by members across all offers. The metric below shows the average commission earned per member."
                isLoading
              />
              <StatCard
                title="Payments Due"
                bgColor="bg-yellow-5"
                widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                value={0}
                metricType="currency"
                tooltip="Shows the total unpaid commissions for members across all offers. The metric below shows the number of members with pending payments."
                isLoading
              />
            </div>
            <DataTable
              columns={dummyOfferTableColumns}
              data={Array(8).fill({})}
              enableColumnResizing
              isLoading
            />
          </>
        );
      case DashboardQueriesStatus.Failed:
        return <Notice type="error" message={queries.error.message} />;
      default:
        const data = queries.stats;
        const hasShopifyOffer = some(
          queries.offers,
          (offer: IDashboardOffer) => offer.source === OFFER_SOURCE.SHOPIFY,
        );

        return (
          <>
            <div className="flex gap-2 px-4 pt-4 flex-wrap">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Tag
                      label={dateRangeSettings.selectedOption === DateRangeOptions.CUSTOM
                        ? `${dateRangeSettings.dateRange?.startDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${dateRangeSettings.dateRange?.endDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                        : dateRangeSettings.selectedOption}
                      bgColor="bg-grey-2"
                      textColor="text-grey-6"
                      onRemove={() => {
                        dateRangeSettings.setEarliestDate(null);
                        dateRangeSettings.onChange(DateRangeOptions.ALL_TIME);
                      }}
                      isRemovable={dateRangeSettings.selectedOption !== DateRangeOptions.ALL_TIME}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                  <TooltipHeader>
                    <TooltipTitle>Date Range Filter</TooltipTitle>
                  </TooltipHeader>
                  <TooltipBody>
                    <TooltipItem>
                      <TooltipItemKey>Selected Date Range</TooltipItemKey>
                      <TooltipItemValue>
                        {dateRangeSettings.selectedOption === DateRangeOptions.CUSTOM
                          ? `${dateRangeSettings.dateRange?.startDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${dateRangeSettings.dateRange?.endDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                          : dateRangeSettings.selectedOption}
                      </TooltipItemValue>
                    </TooltipItem>
                  </TooltipBody>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>

              {selectedOfferType && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Tag
                        label={`Offer Type: ${OFFER_TYPE_LABELS[selectedOfferType] || selectedOfferType}`}
                        bgColor="bg-grey-2"
                        textColor="text-grey-6"
                        onRemove={() => setSelectedOfferType(STATS_OFFER_TYPE.ALL_OFFERS)}
                        isRemovable={selectedOfferType !== STATS_OFFER_TYPE.ALL_OFFERS}
                        className="cursor-pointer"
                        onClick={() => setIsDrawerOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                    <TooltipHeader>
                      <TooltipTitle>Offer Type Filter</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemKey>Selected Offer Types</TooltipItemKey>
                        <TooltipItemValue>
                          {selectedOfferType === STATS_OFFER_TYPE.ALL_OFFERS
                            ? 'All Offer Types'
                            : OFFER_TYPE_LABELS[selectedOfferType] || selectedOfferType}
                        </TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                    <TooltipArrow />

                  </TooltipContent>
                </Tooltip>
              )}

              {selectedFilters.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Tag
                        label={`Status: ${OFFER_STATUS_LABELS[selectedFilters[0]] || selectedFilters[0]}${selectedFilters.length > 1 ? `, +${selectedFilters.length - 1}` : ''}`}
                        bgColor="bg-grey-2"
                        textColor="text-grey-6"
                        onRemove={() => setSelectedFilters([])}
                        isRemovable
                        className="cursor-pointer"
                        onClick={() => setIsDrawerOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                    <TooltipHeader>
                      <TooltipTitle>Status Filter</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemKey>Selected Statuses</TooltipItemKey>
                        <TooltipItemValue>
                          {selectedFilters.length === 0
                            ? 'All Statuses'
                            : selectedFilters.map((filter) => OFFER_STATUS_LABELS[filter] || filter).join(', ')}
                        </TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              )}

              {selectedPrograms.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Tag
                        label={`${getProgramName(programs, selectedPrograms[0])}${selectedPrograms.length > 1 ? `, +${selectedPrograms.length - 1}` : ''}`}
                        bgColor="bg-grey-2"
                        textColor="text-grey-6"
                        onRemove={() => setSelectedPrograms([])}
                        isRemovable
                        className="cursor-pointer"
                        onClick={() => setIsDrawerOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                    <TooltipHeader>
                      <TooltipTitle>Project Filter</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemKey>Selected Projects</TooltipItemKey>
                        <TooltipItemValue>
                          {selectedPrograms.length === 0
                            ? 'All Projects'
                            : selectedPrograms.map((programId) => getProgramName(programs, programId)).join(', ')}
                        </TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                  </TooltipContent>
                </Tooltip>
              )}

              {offerNumericFilters && Object.keys(offerNumericFilters).some((key) => offerNumericFilters[key]?.length > 0) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Tag
                        label="KPI Filters"
                        bgColor="bg-grey-2"
                        textColor="text-grey-6"
                        onRemove={() => setOfferNumericFilters({})}
                        isRemovable
                        className="cursor-pointer"
                        onClick={() => setIsDrawerOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                    <TooltipHeader>
                      <TooltipTitle>KPI Filters</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemKey>Applied Filters</TooltipItemKey>
                        <TooltipItemValue>
                          {Object.entries(offerNumericFilters || {}).map(([columnId, filterValues]) => {
                            if (!filterValues || filterValues.length === 0) return null;
                            const columnLabels = {
                              clicks: 'Clicks',
                              conversions: 'Conversions',
                              salesAmount: 'Sales Amount',
                              avgSale: 'Average Sale',
                              payoutEarned: 'Commission Earned',
                              payoutMade: 'Payout Made'
                            };
                            const operatorLabels = {
                              gt: '>',
                              lt: '<',
                              gte: '≥',
                              lte: '≤',
                              eq: '=',
                              between: 'between'
                            };
                            const [operator, value] = filterValues;
                            return (
                              <div key={columnId} className="text-xs">
                                {columnLabels[columnId] || columnId}: {operator === 'between'
                                  ? `${operatorLabels[operator]} ${value.split(',')[0]} - ${value.split(',')[1]}`
                                  : `${operatorLabels[operator] || operator} ${value}`}
                              </div>
                            );
                          })}
                        </TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              )}

              {offerSelectedStatusFilters && offerSelectedStatusFilters.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Tag
                        label={`Status: ${offerSelectedStatusFilters.length} selected`}
                        bgColor="bg-grey-2"
                        textColor="text-grey-6"
                        onRemove={() => setOfferSelectedStatusFilters([])}
                        isRemovable
                        className="cursor-pointer"
                        onClick={() => setIsDrawerOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                    <TooltipHeader>
                      <TooltipTitle>Status Filters</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemKey>Selected Statuses</TooltipItemKey>
                        <TooltipItemValue>
                          {offerSelectedStatusFilters.map((status) => status).join(', ')}
                        </TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 p-4">
              <StatCard
                title="Members"
                bgColor="bg-purple-5"
                widgetIcon={<UserGroupIcon className="text-grey-0" fontSize={20} />}
                value={data.members}
                metricType="count"
                subHeading={`${data?.memberConversionCount} with Conversions`}
                tooltip="The number of members added across all offers. The metric below shows how many of them have driven at least one conversion."
              />
              <StatCard
                title="Conversions"
                bgColor="bg-blue-5"
                // widgetIcon={<GlobeStandIcon className="text-white" fontSize={20} />}
                widgetIcon={<FontAwesomeIcon icon={faGlobe} className="text-white" fontSize={20} />}
                value={data.conversions}
                metricType="count"
                subHeading={`${data.clicks} clicks`}
                primaryPopoverText={`${data.conversions?.toLocaleString('en-US')} conversions`}
                tooltip="Shows the number of conversions driven by members across all offers. The metric below shows the number of clicks."
              />
              <StatCard
                title="Total Sales"
                bgColor="bg-teal-5"
                widgetIcon={<CircleDollarIcon className="text-white" fontSize={20} />}
                value={Number(data.sales.toFixed(2))}
                metricType="currency"
                subHeading={`$${(data.avgSale && data?.memberConversionCount ? (data.sales / data?.memberConversionCount).toFixed(2) : 0)} Sales per Member`}
                primaryPopoverText={`$${data.sales?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Sales`}
                tooltip="Shows the total sales driven by members across all offers. The metric below shows the average sales per member."
              />
              <StatCard
                title="Commissions Earned"
                bgColor="bg-lime-5"
                widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                value={Number(data.payoutEarned.toFixed(2))}
                metricType="currency"
                subHeading={`$${data.members && data?.memberConversionCount ? (data.payoutEarned / data?.memberConversionCount).toFixed(2) : '0.00'} Avg per Member`}
                primaryPopoverText={`$${data.payoutEarned?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Commissions Earned`}
                tooltip="Shows the total commissions earned by members across all offers. The metric below shows the average commission earned per member."
              />
              <StatCard
                title="Payments Due"
                bgColor="bg-yellow-5"
                widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                value={Number((data.payoutEarned - data.payoutMade).toFixed(2))}
                metricType="currency"
                subHeading={`${data.membersWithPayoutDue} Members`}
                primaryPopoverText={`$${data.payoutEarned - data.payoutMade} Payments Due`}
                tooltip="Shows the total unpaid commissions for members across all offers. The metric below shows the number of members with pending payments."
              />
            </div>
            {hasShopifyOffer && queries.missingShopifyCredentials && (
              <div className="px-4">
                <ShopifyScopesMissing onClick={() => {
                  history.push({
                    ...location,
                    pathname: `/settings/${encodeURIComponent(shopifyAppId)}`,
                  });
                }}
                />
              </div>
            )}

            <div className="px-4">
              <RefreshOfferTable
                data={queries.offers}
                missingShopifyCredentials={queries.missingShopifyCredentials}
                onClickAddOffer={queries.offerActions.onClickAddOffer}
                onClickExport={queries.offerActions.onClickExport}
                onOfferClicked={queries.offerActions.onOfferClicked}
                sources={sources}
                programs={programs}
                baseUri={baseUri}
                clientName={clientName}
                dateRangeSettings={dateRangeSettings}
                selectedOfferType={selectedOfferType}
                setSelectedOfferType={setSelectedOfferType}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                selectedPrograms={selectedPrograms}
                numericFilters={offerNumericFilters}
                selectedStatusFilters={offerSelectedStatusFilters}
              />
            </div>
          </>
        );
    }
  };
  return (
    <>
      {getDashboardPageContents()}
    </>
  );
};
